import { useCallback } from 'react';
import { 
  NODE_WIDTH, NODE_HEIGHT, HORIZONTAL_GAP, ZONE_PADDING, INTER_CAMPAIGN_GAP 
} from './constants';
import { buildPositionMap, createOverlapChecker } from './positionHelpers';
import { getAllConditionTaskIds } from '../../../shared/ConditionsDropdown/utils/conditionHelpers';

/**
 * Hook for finding position for new nodes in multi-campaign mode
 */
export function useFindPositionInCampaign({
  tasks,
  nodePositions,
  nodeDimensions,
  customPositionsRef,
  getViewportBounds,
  findEmptySpaceInViewport,
}) {
  /**
   * Find position for a new node in a specific campaign
   * 
   * When visible: find space within campaign zone that's also in viewport
   * When not visible: expand right and shift other campaigns
   */
  const findPositionInCampaign = useCallback((campaignId, allTasks) => {
    const SPACING = HORIZONTAL_GAP;
    const positionMap = buildPositionMap(nodePositions, customPositionsRef);
    
    // Get campaign tasks and their positions
    const campaignTasks = allTasks.filter(t => t.sourceCampaign?.id === campaignId);
    if (campaignTasks.length === 0) {
      return { position: findEmptySpaceInViewport(), campaignAdjustments: null };
    }
    
    const campaignPositions = campaignTasks.map(t => {
      const pos = positionMap[t.id];
      const dims = nodeDimensions[t.id] || { width: NODE_WIDTH, height: NODE_HEIGHT };
      return pos ? { id: t.id, ...pos, width: dims.width, height: dims.height, task: t } : null;
    }).filter(Boolean);
    
    if (campaignPositions.length === 0) {
      return { position: findEmptySpaceInViewport(), campaignAdjustments: null };
    }
    
    // Calculate campaign bounds
    const campaignBounds = {
      minX: Math.min(...campaignPositions.map(p => p.x)),
      maxX: Math.max(...campaignPositions.map(p => p.x + p.width)),
      minY: Math.min(...campaignPositions.map(p => p.y)),
      maxY: Math.max(...campaignPositions.map(p => p.y + p.height)),
    };
    
    const viewport = getViewportBounds();
    const isCampaignVisible = (
      campaignBounds.maxX > viewport.left &&
      campaignBounds.minX < viewport.right &&
      campaignBounds.maxY > viewport.top &&
      campaignBounds.minY < viewport.bottom
    );
    
    const overlapsWithCampaignNode = createOverlapChecker(campaignPositions, SPACING);
    
    const isInViewport = (x, y) => {
      const margin = 20;
      return (
        x >= viewport.left + margin &&
        x + NODE_WIDTH <= viewport.right - margin &&
        y >= viewport.top + margin &&
        y + NODE_HEIGHT <= viewport.bottom - margin
      );
    };
    
    // ═══════════════════════════════════════════════════════════════
    // CASE 1: Campaign IS visible - place to the right of existing nodes
    // ═══════════════════════════════════════════════════════════════
    if (isCampaignVisible) {
      // Primary: Place to the right of the rightmost node (consistent with single-campaign behavior)
      const rightExpansion = { x: campaignBounds.maxX + SPACING, y: campaignBounds.minY };
      if (!overlapsWithCampaignNode(rightExpansion.x, rightExpansion.y)) {
        // Calculate the new campaign max X after adding this node
        const newCampaignMaxX = rightExpansion.x + NODE_WIDTH;
        
        // Check if we need to shift campaigns to the right
        const campaignAdjustments = calculateCampaignShifts(
          campaignId, campaignBounds.minX, newCampaignMaxX,
          allTasks, positionMap, nodeDimensions
        );
        
        return { position: rightExpansion, campaignAdjustments };
      }
      
      // If right is blocked, try other expansion directions (no shifts needed for these)
      const expansionOptions = [
        { x: campaignBounds.minX, y: campaignBounds.maxY + SPACING, dir: 'bottom' },
        { x: campaignBounds.minX, y: campaignBounds.minY - NODE_HEIGHT - SPACING, dir: 'top' },
        { x: campaignBounds.minX - NODE_WIDTH - SPACING, y: campaignBounds.minY, dir: 'left' },
      ];
      
      for (const opt of expansionOptions) {
        if (opt.x >= 0 && opt.y >= 0 && !overlapsWithCampaignNode(opt.x, opt.y)) {
          return { position: { x: opt.x, y: opt.y }, campaignAdjustments: null };
        }
      }
      
      // Last resort: scan for any empty space within campaign zone
      const SCAN_STEP = 40;
      for (let y = campaignBounds.minY; y <= campaignBounds.maxY; y += SCAN_STEP) {
        for (let x = campaignBounds.minX; x <= campaignBounds.maxX; x += SCAN_STEP) {
          if (!overlapsWithCampaignNode(x, y)) {
            return { position: { x, y }, campaignAdjustments: null };
          }
        }
      }
      
      // Absolute fallback: expand bottom regardless
      const bottomExpansion = { x: campaignBounds.minX, y: campaignBounds.maxY + SPACING };
      return { position: bottomExpansion, campaignAdjustments: null };
    }
    
    // ═══════════════════════════════════════════════════════════════
    // CASE 2: Campaign is NOT visible - expand right + shift others
    // ═══════════════════════════════════════════════════════════════
    
    // Find top-level nodes (no dependencies within this campaign)
    const campaignTaskIds = new Set(campaignTasks.map(t => t.id));
    const topLevelNodes = campaignPositions.filter(p => {
      const task = p.task;
      let depIds = getAllConditionTaskIds(task.unlockConditions, allTasks);
      if (depIds.length === 0 && task.dependsOn && task.dependsOn.length > 0) {
        depIds = task.dependsOn.map(d => typeof d === 'string' ? d : d?.taskId).filter(Boolean);
      }
      if (depIds.length === 0) return true;
      return !depIds.some(depId => campaignTaskIds.has(depId));
    });
    
    const nodesToCheck = topLevelNodes.length > 0 ? topLevelNodes : campaignPositions;
    const rightmostNode = nodesToCheck.reduce((max, curr) => {
      return (curr.x + curr.width > max.x + max.width) ? curr : max;
    }, nodesToCheck[0]);
    
    const newX = rightmostNode.x + rightmostNode.width + SPACING;
    const newY = campaignBounds.minY;
    const newCampaignMaxX = newX + NODE_WIDTH;
    
    // Calculate campaign adjustments using same logic as drag code
    const campaignAdjustments = calculateCampaignShifts(
      campaignId, campaignBounds.minX, newCampaignMaxX,
      allTasks, positionMap, nodeDimensions
    );
    
    return { position: { x: newX, y: newY }, campaignAdjustments };
  }, [tasks, nodePositions, nodeDimensions, customPositionsRef, getViewportBounds, findEmptySpaceInViewport]);

  return { findPositionInCampaign };
}

/**
 * Calculate how much other campaigns need to shift
 * 
 * Key insight: When expanding campaign A to the right, ALL campaigns to its right
 * need to shift by the same amount - not just the immediate neighbor.
 * Otherwise A pushes B, but B overlaps C.
 */
function calculateCampaignShifts(campaignId, campaignMinX, newCampaignMaxX, allTasks, positionMap, nodeDimensions) {
  const campaignIds = [...new Set(allTasks.map(t => t.sourceCampaign?.id).filter(Boolean))];
  const otherCampaignBounds = {};
  
  campaignIds.forEach(cId => {
    if (cId === campaignId) return;
    const otherTasks = allTasks.filter(t => t.sourceCampaign?.id === cId);
    const otherPositions = otherTasks.map(t => {
      const pos = positionMap[t.id];
      const width = nodeDimensions[t.id]?.width || NODE_WIDTH;
      return pos ? { ...pos, width } : null;
    }).filter(Boolean);
    
    if (otherPositions.length > 0) {
      otherCampaignBounds[cId] = {
        minX: Math.min(...otherPositions.map(p => p.x)),
        maxX: Math.max(...otherPositions.map(p => p.x + p.width)),
      };
    }
  });
  
  // Find the immediate neighbor to the right (smallest minX > campaignMinX)
  const campaignsToRight = Object.entries(otherCampaignBounds)
    .filter(([_, bounds]) => bounds.minX > campaignMinX)
    .sort((a, b) => a[1].minX - b[1].minX);
  
  if (campaignsToRight.length === 0) return null;
  
  // Calculate shift needed based on immediate neighbor
  const [nearestCampaignId, nearestBounds] = campaignsToRight[0];
  const newCampaignZoneRight = newCampaignMaxX + ZONE_PADDING;
  const nearestZoneLeft = nearestBounds.minX - ZONE_PADDING;
  const currentGap = nearestZoneLeft - newCampaignZoneRight;
  
  if (currentGap >= INTER_CAMPAIGN_GAP) {
    // No shift needed - there's enough gap
    return null;
  }
  
  // Calculate shift amount needed
  const shiftAmount = INTER_CAMPAIGN_GAP - currentGap;
  
  // Apply this shift to ALL campaigns to the right, not just the neighbor
  const campaignAdjustments = {};
  campaignsToRight.forEach(([cId]) => {
    const tasksInCampaign = allTasks.filter(t => t.sourceCampaign?.id === cId);
    const shifts = {};
    tasksInCampaign.forEach(task => {
      const currentPos = positionMap[task.id];
      if (currentPos) {
        shifts[task.id] = { x: currentPos.x + shiftAmount, y: currentPos.y };
      }
    });
    campaignAdjustments[cId] = { xOffset: shiftAmount, positionShifts: shifts };
  });
  
  return campaignAdjustments;
}
